import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsApiReady } from "../hooks/use-api-ready";
import {
  groupKitSkillControllerGetSingle,
  groupKitSkillControllerPatchSkill,
  type GetGroupSkillsResponseDto,
  type PatchSkillBodyDto,
} from "../lib/gen-api";
import { queryKeys } from "../lib/query-keys";

export const useSkillData = (
  groupUuid: string | null,
  kitUuid: string | null,
  skillUuid: string | null,
) => {
  const queryClient = useQueryClient();

  const {
    data: skill,
    isLoading,
    error,
  } = useQuery({
    enabled: useIsApiReady() && !!groupUuid && !!kitUuid && !!skillUuid,
    initialData: null,
    queryKey: queryKeys.kitSkill(groupUuid!, kitUuid!, skillUuid!),
    queryFn: () =>
      groupKitSkillControllerGetSingle({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          skillUuid: skillUuid!,
        },
      }).then((res) => res.data ?? null),
  });

  const { mutateAsync: updateSkill } = useMutation({
    mutationFn: async (vars: PatchSkillBodyDto) => {
      if (!skill) {
        throw new Error("Skill not found");
      }

      return groupKitSkillControllerPatchSkill({
        path: {
          groupUuid: groupUuid!,
          kitUuid: kitUuid!,
          skillUuid: skillUuid!,
        },
        body: {
          name: vars.name ?? skill.name,
          description: vars.description ?? skill.description,
          metadataDescription:
            vars.metadataDescription ?? skill.metadataDescription,
          content: vars.content ?? skill.content,
          category: vars.category ?? skill.category ?? undefined,
        },
      });
    },
    onMutate: async (vars) => {
      if (!skill || !groupUuid || !kitUuid || !skillUuid) {
        return;
      }

      await queryClient.cancelQueries({
        queryKey: queryKeys.kitSkill(groupUuid, kitUuid, skillUuid),
      });

      const previousSkill = queryClient.getQueryData(
        queryKeys.kitSkill(groupUuid, kitUuid, skillUuid),
      );

      queryClient.setQueryData(
        queryKeys.kitSkill(groupUuid, kitUuid, skillUuid),
        (old: GetGroupSkillsResponseDto["data"][number] | null) => {
          if (!old) return null;
          return {
            ...old,
            name: vars.name ?? old.name,
            description: vars.description ?? old.description,
            metadataDescription:
              vars.metadataDescription ?? old.metadataDescription,
            content: vars.content ?? old.content,
          };
        },
      );

      return { previousSkill };
    },
    onError: (_err, _vars, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSkill && groupUuid && kitUuid && skillUuid) {
        queryClient.setQueryData(
          queryKeys.kitSkill(groupUuid, kitUuid, skillUuid),
          context.previousSkill,
        );
      }
    },
    onSettled: () => {
      if (groupUuid && kitUuid && skillUuid) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitSkill(groupUuid, kitUuid, skillUuid),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.kitSkills(groupUuid, kitUuid),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.groupSkills({
            groupUuid: groupUuid!,
          }),
        });
      }
    },
  });

  return { skill, isLoading, error, updateSkill };
};
